import { useCallback, type MouseEvent } from 'react'
import { createPath, useNavigate, type NavigateOptions, type To } from 'react-router-dom'

/**
 * Something the site's header, footer or account menu is about to do that takes the person off the page: follow an
 * in-app link (`to`: its path, search and hash), sign out, or switch organisation (superadmins), which reloads the
 * app. `proceed()` does exactly that, whenever the page is ready, and only once: calling it again, or after the page
 * let the leave through, does nothing.
 */
export type SiteLeave =
  | { kind: 'navigate'; to: string; proceed(): void }
  | { kind: 'sign_out'; proceed(): void }
  | { kind: 'switch_organization'; organizationId: string; proceed(): void }

/** Asked before a SiteLeave. Return false to hold it: nothing leaves, and the page calls `leave.proceed()` later (for
 *  example after "End your live?"). Anything else lets it through; so does calling `proceed()` before returning. */
export type BeforeLeave = (leave: SiteLeave) => boolean | void

/** A SiteLeave before its `proceed`. */
type LeaveRequest = { [Kind in SiteLeave['kind']]: Omit<Extract<SiteLeave, { kind: Kind }>, 'proceed'> }[SiteLeave['kind']]

/**
 * Asks `beforeLeave` about `request`, whose `proceed()` runs `leave` once at most. True when the caller should leave
 * its own way now: there's no `beforeLeave`, or it let the leave through untouched (a later `proceed()` then does
 * nothing). False when the page held the leave or already proceeded with it, so the caller mustn't leave again.
 */
export function mayLeave(beforeLeave: BeforeLeave | undefined, request: LeaveRequest, leave: () => void) {
  if (!beforeLeave) return true
  let done = false
  const proceed = () => {
    if (done) return
    done = true
    leave()
  }
  const verdict = beforeLeave({ ...request, proceed } as SiteLeave)
  if (done || verdict === false) return false
  done = true
  return true
}

/** A click that leaves in this tab: the main button with no modifier key, not already handled, on a link that doesn't
 *  open elsewhere or download. Enter on a focused link clicks it, so it counts. */
function leavesThisTab(event: MouseEvent<HTMLAnchorElement>) {
  const link = event.currentTarget
  return !event.defaultPrevented && event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey
    && (!link.target || link.target === '_self') && !link.hasAttribute('download')
}

/** Asks `beforeLeave` before a router link is followed: call the returned function from the link's click with its
 *  `to`, and its `replace` and `state` if it has them. When the page holds the leave (or proceeds while asked) the
 *  click is prevented, and `proceed()` navigates there as the link would have. Without `beforeLeave`, for clicks that
 *  open a new tab or window, and for links outside the router (no `to`, such as `href` links) it does nothing. */
export function useLeaveGuard(beforeLeave?: BeforeLeave) {
  const navigate = useNavigate()
  return useCallback((event: MouseEvent<HTMLAnchorElement>, to: To | undefined, options?: Pick<NavigateOptions, 'replace' | 'state'>) => {
    if (!beforeLeave || to === undefined || !leavesThisTab(event)) return
    const request = { kind: 'navigate' as const, to: typeof to === 'string' ? to : createPath(to) }
    if (!mayLeave(beforeLeave, request, () => navigate(to, options))) event.preventDefault()
  }, [beforeLeave, navigate])
}
